import { DeploymentEnv } from '@enums/deployment-env'
import { DEPLOYMENT_ENV, REACT_APP_OKTA_ISSUER } from '../../../config'
import { getCurrentAuthenticatedUser } from '@api/cognito'
import { ApiError } from '@util/api-error'
import { OktaAuth } from '@okta/okta-auth-js'

type PossibleValuesType = boolean | string | number | object | null | []

/**
 * a basic type to extend more defined response objects from for API
 * payloads
 */
export type ApiResponse = {
  [key: string]: PossibleValuesType
}

type Body = {
  [key: string]: PossibleValuesType
}

const PATHS = new Map([
  [DeploymentEnv.local, 'https://staging-api.thriveglobal.com'],
  [DeploymentEnv.develop, 'https://staging-api.thriveglobal.com'],
  [DeploymentEnv.staging, 'https://staging-api.thriveglobal.com'],
  [DeploymentEnv.preprod, 'https://api.thriveglobal.com'],
  [DeploymentEnv.production, 'https://api.thriveglobal.com'],
])

const deploymentEnv = DEPLOYMENT_ENV as DeploymentEnv
const base = PATHS.get(deploymentEnv)

type Options = {
  method: 'GET' | 'POST'
  path: string
  body?: Body
  queryParams?: Record<string, string>
}

type GetFetchResponse = <T extends ApiResponse>(
  options: Options,
  bypassAuth?: boolean
) => Promise<T>

/**
 * boilerplate for making authenticated (with a Cognito token) and unauthenticated
 * requests to the Thrive Platform API
 */
const getFetchResponse: GetFetchResponse = async (
  { method, path, body, queryParams },
  bypassAuth = false
) => {
  const url = new URL(`${base}${path}`)

  type Headers = Record<string, string>
  const headers: Headers = {
    'Content-Type': 'application/json',
  }

  // require the access token for endpoints that require authentication
  if (!bypassAuth) {
    const cognitoAccessToken = await getAccessToken()
    headers.Authorization = cognitoAccessToken
  }

  type FormattedOptions = { method: string; headers: Headers; body?: string }
  const formattedOptions: FormattedOptions = { method, headers }

  if (method === 'GET') {
    url.search = new URLSearchParams(queryParams).toString()
  }

  if (method === 'POST') {
    formattedOptions.body = JSON.stringify(body)
  }

  const res = await fetch(url.toString(), formattedOptions)

  if (res.ok) {
    const payload = await res.json()
    return payload
  }

  // procure the message and code from the platform payload
  const unpackedError: ApiError = await res.json()

  throw unpackedError
}

export default getFetchResponse

async function getAccessToken() {
  const issuer = REACT_APP_OKTA_ISSUER
  const oktaAuth = new OktaAuth({ issuer })
  const isOktaAuthenticated = await oktaAuth.isAuthenticated()
  if (isOktaAuthenticated) {
    return (await oktaAuth.tokenManager.get('accessToken')).value
  }

  const user = await getCurrentAuthenticatedUser()

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const accessToken = user?.signInUserSession?.accessToken.jwtToken

  return accessToken
}
